/**
 * Japanese era (和暦) conversion utilities.
 * Ported from RA2 public/js/ui.js to keep dist datepicker logic.
 */

/** 令和 (Reiwa) start: 2019-05-01 */
const REIWA = new Date(2019, 4, 1);
/** 平成 (Heisei) start: 1989-01-08 */
const HEISEI = new Date(1989, 0, 8);
/** 昭和 (Showa) start: 1926-12-25 */
const SHOWA = new Date(1926, 11, 25);
/** 大正 (Taisho) start: 1912-07-30 */
const TAISHO = new Date(1912, 6, 30);
/** 明治 (Meiji) start: 1868-01-25 */
const MEIJI = new Date(1868, 0, 25);

/**
 * Convert Gregorian year (and optional month) to 和暦 string (e.g. "令和06").
 * No798: 来年の年号が決まるまで平成で表示 → Reiwa used for year >= 2019.
 */
export function convertWareki(year: number, month?: string | number): string {
  const y = year;
  const m = month != null && month !== '' ? Number(month) : undefined;
  if (m != null && !Number.isNaN(m)) {
    const yyyymm = y * 100 + m;
    if (yyyymm > 201904) return '令和' + String(y - 2018).padStart(2, '0');
    if (yyyymm > 198812) return '平成' + String(y - 1988).padStart(2, '0');
    if (yyyymm > 192611) return '昭和' + String(y - 1925).padStart(2, '0');
    if (yyyymm > 191207) return '大正' + String(y - 1911).padStart(2, '0');
    if (yyyymm > 186712) return '明治' + String(y - 1867).padStart(2, '0');
    return '';
  }
  if (y > 2018) return '令和' + String(y - 2018).padStart(2, '0');
  if (y > 1988) return '平成' + String(y - 1988).padStart(2, '0');
  if (y > 1925) return '昭和' + String(y - 1925).padStart(2, '0');
  if (y > 1911) return '大正' + String(y - 1911).padStart(2, '0');
  if (y > 1867) return '明治' + String(y - 1867).padStart(2, '0');
  return '';
}

const GENGOPattern = /^(令和|平成|昭和|大正|明治)(\d+)/;

/**
 * Convert 和暦 string (e.g. "令和06", "令和6") to Gregorian year number.
 */
export function convertSeireki(wareki: string): number {
  const m = String(wareki).match(GENGOPattern);
  if (!m) return NaN;
  const gengo = m[1];
  const year = parseInt(m[2], 10);
  if (Number.isNaN(year)) return NaN;
  if (gengo === '令和') return year + 2018;
  if (gengo === '平成') return year + 1988;
  if (gengo === '昭和') return year + 1925;
  if (gengo === '大正') return year + 1911;
  if (gengo === '明治') return year + 1867;
  return year;
}

/**
 * Convert year, month, day to 和暦 era part only (e.g. "令和06").
 */
export function convertWarekiDetail(year: number, month: number, day: number): string {
  const date = new Date(year, month - 1, day);
  if (date.getTime() >= REIWA.getTime()) return '令和' + String(year - 2018).padStart(2, '0');
  if (date.getTime() >= HEISEI.getTime()) return '平成' + String(year - 1988).padStart(2, '0');
  if (date.getTime() >= SHOWA.getTime()) return '昭和' + String(year - 1925).padStart(2, '0');
  if (date.getTime() >= TAISHO.getTime()) return '大正' + String(year - 1911).padStart(2, '0');
  if (date.getTime() >= MEIJI.getTime()) return '明治' + String(year - 1867).padStart(2, '0');
  return '';
}

/**
 * Convert year, month to 和暦 era part (month view).
 */
export function convertWarekiMonth(year: number, month: number): string {
  const date = new Date(year, month - 1);
  if (date.getTime() >= REIWA.getTime()) return '令和' + String(year - 2018).padStart(2, '0');
  if (date.getTime() >= HEISEI.getTime()) return '平成' + String(year - 1988).padStart(2, '0');
  if (date.getTime() >= SHOWA.getTime()) return '昭和' + String(year - 1925).padStart(2, '0');
  if (date.getTime() >= TAISHO.getTime()) return '大正' + String(year - 1911).padStart(2, '0');
  if (date.getTime() >= MEIJI.getTime()) return '明治' + String(year - 1867).padStart(2, '0');
  return '';
}

/**
 * Parse 和暦 display string to 西暦 "YYYY/MM/DD" or "YYYY/MM".
 * Supports 1- or 2-digit month/day (e.g. 2月5日).
 */
export function convertSeirekiWrap(wareki: string): string {
  if (!wareki || !GENGOPattern.test(wareki)) return wareki;
  const digitParts = wareki.match(/\d+/g);
  if (!digitParts || digitParts.length < 1) return wareki;
  const eraStr = wareki.match(GENGOPattern);
  const y = eraStr ? convertSeireki(eraStr[0]) : NaN;
  if (Number.isNaN(y)) return wareki;
  digitParts[0] = String(y);
  if (digitParts.length >= 3) return `${digitParts[0]}/${digitParts[1].padStart(2, '0')}/${digitParts[2].padStart(2, '0')}`;
  if (digitParts.length >= 2) return `${digitParts[0]}/${digitParts[1].padStart(2, '0')}`;
  return digitParts[0];
}

/**
 * Format 西暦 "YYYY/MM/DD" to 和暦 display "令和06年01月27日".
 */
export function convertWarekiWrap(seireki: string): string {
  if (!seireki || seireki.indexOf('/') === -1) return seireki;
  const [y, m, d] = seireki.split('/');
  const wareki = convertWarekiDetail(Number(y), Number(m), Number(d));
  return `${wareki}年${m}月${d}日`;
}
